import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Chip,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
  TextField,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Close,
  Email,
  Phone,
  Person,
  CalendarToday,
  TrendingUp,
  AttachMoney,
  CheckCircle,
  Block,
  ContentCopy,
  Edit,
  Delete,
  RestartAlt,
} from '@mui/icons-material';
import { partnershipsService, PartnershipDashboard } from '@/services/partnershipsService';
import toast from 'react-hot-toast';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface PartnershipDetailModalProps {
  open: boolean;
  partnershipId: string;
  onClose: () => void;
  onReactivate?: () => void;
  onDeactivate?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const PartnershipDetailModal: React.FC<PartnershipDetailModalProps> = ({
  open,
  partnershipId,
  onClose,
  onReactivate,
  onDeactivate,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState<PartnershipDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customRateDialogOpen, setCustomRateDialogOpen] = useState(false);
  const [customRateValue, setCustomRateValue] = useState('');
  const [customRateLoading, setCustomRateLoading] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    companyName: '',
    email: '',
    phone: '',
    contactPerson: '',
    notes: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [resetMenuAnchor, setResetMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning' as 'warning' | 'error' | 'info' | 'question',
  });

  useEffect(() => {
    if (open && partnershipId) {
      loadDashboard();
    }
  }, [open, partnershipId]);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await partnershipsService.getPartnershipDashboard(partnershipId);
      if (response.success && response.data) {
        setDashboard(response.data);
      } else {
        setError(response.error || 'Failed to load dashboard');
      }
    } catch (err) {
      setError('Failed to load partnership dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '₦0';
    }
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleCopyReferralCode = () => {
    if (dashboard?.partnership?.referralCode) {
      navigator.clipboard.writeText(dashboard.partnership.referralCode);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const handleOpenCustomRateDialog = () => {
    setCustomRateValue(dashboard?.partnership?.customCommissionRate?.toString() || '');
    setCustomRateDialogOpen(true);
  };

  const handleSetCustomRate = async () => {
    const rate = parseFloat(customRateValue);
    if (isNaN(rate) || rate <= 0 || rate > 100) {
      toast.error('Please enter a valid commission rate between 0 and 100');
      return;
    }

    setCustomRateLoading(true);
    try {
      const response = await partnershipsService.setCustomCommissionRate(partnershipId, rate);
      if (response.success) {
        toast.success(response.message || 'Custom commission rate set successfully');
        setCustomRateDialogOpen(false);
        await loadDashboard();
      } else {
        toast.error(response.error || 'Failed to set custom commission rate');
      }
    } catch (err) {
      toast.error('Failed to set custom commission rate');
    } finally {
      setCustomRateLoading(false);
    }
  };

  const handleRemoveCustomRate = async () => {
    if (!window.confirm('Remove custom commission rate? Partnership will revert to default rate.')) {
      return;
    }

    setCustomRateLoading(true);
    try {
      const response = await partnershipsService.removeCustomCommissionRate(partnershipId);
      if (response.success) {
        toast.success(response.message || 'Custom commission rate removed');
        await loadDashboard();
      } else {
        toast.error(response.error || 'Failed to remove custom commission rate');
      }
    } catch (err) {
      toast.error('Failed to remove custom commission rate');
    } finally {
      setCustomRateLoading(false);
    }
  };

  const handleSuspendPartnership = async () => {
    setSuspendLoading(true);
    try {
      const response = await partnershipsService.suspendPartnership(partnershipId, suspendReason);
      if (response.success) {
        toast.success(response.message || 'Partnership suspended successfully');
        setSuspendDialogOpen(false);
        setSuspendReason('');
        await loadDashboard();
        onClose();
      } else {
        toast.error(response.error || 'Failed to suspend partnership');
      }
    } catch (err) {
      toast.error('Failed to suspend partnership');
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleOpenEditDialog = () => {
    setEditData({
      companyName: dashboard?.partnership?.companyName || '',
      email: dashboard?.partnership?.email || '',
      phone: dashboard?.partnership?.phone || '',
      contactPerson: dashboard?.partnership?.contactPerson || '',
      notes: dashboard?.partnership?.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdatePartnership = async () => {
    setEditLoading(true);
    try {
      const response = await partnershipsService.updatePartnership(partnershipId, {
        companyName: editData.companyName,
        email: editData.email,
        phone: editData.phone,
        contactPerson: editData.contactPerson,
        notes: editData.notes,
      });
      if (response.success) {
        toast.success(response.message || 'Partnership updated successfully');
        setEditDialogOpen(false);
        await loadDashboard();
      } else {
        toast.error(response.error || 'Failed to update partnership');
      }
    } catch (err) {
      toast.error('Failed to update partnership');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetVolumes = (resetType: 'yearly' | 'monthly') => {
    setResetMenuAnchor(null);
    const resetTypeLabel = resetType === 'yearly' ? 'Year-to-Date (YTD)' : 'Month-to-Date (MTD)';
    const resetTypeDescription = resetType === 'yearly'
      ? 'This will calculate the new tier based on the full year performance and reset YTD volumes to zero.'
      : 'This will calculate the new tier based on last month\'s performance and reset MTD volumes to zero.';

    setConfirmDialog({
      open: true,
      title: `Reset ${resetTypeLabel} Volumes`,
      message: `Are you sure you want to reset ${resetTypeLabel} volumes for ${dashboard?.partnership.companyName}?\n\n${resetTypeDescription}\n\nThis action cannot be undone.`,
      severity: 'warning',
      onConfirm: async () => {
        try {
          const response = await partnershipsService.resetPartnershipVolumes({
            resetType,
            partnershipId,
            confirm: true,
          });
          if (response.success) {
            // Check if tier information is in the response
            const tierInfo = response.data?.newTier;
            if (tierInfo) {
              toast.success(
                `${resetTypeLabel} volumes reset successfully! New tier: ${tierInfo.tierName} (${tierInfo.commissionRate}%)`,
                { duration: 5000 }
              );
            } else {
              toast.success(response.message || `${resetTypeLabel} volumes reset successfully`);
            }
            await loadDashboard();
          } else {
            toast.error(response.error || `Failed to reset ${resetTypeLabel} volumes`);
          }
        } catch (error) {
          toast.error(`Failed to reset ${resetTypeLabel} volumes`);
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              {loading ? 'Loading...' : dashboard?.partnership?.companyName || 'Partnership Details'}
            </Typography>
            {dashboard?.partnership && !loading && (
              <>
                <Chip
                  label={dashboard.partnership.isActive ? 'Active' : 'Inactive'}
                  color={dashboard.partnership.isActive ? 'success' : 'default'}
                  size="small"
                />
                <IconButton size="small" onClick={handleOpenEditDialog} title="Edit Partnership Details">
                  <Edit fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {error && !loading && (
          <Box py={4}>
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={loadDashboard}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Unable to load partnership dashboard. Please try again or contact support if the problem persists.
            </Typography>
          </Box>
        )}

        {dashboard?.partnership && !loading && (
          <>
            {/* Referral Code Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Referral Code
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" fontWeight="bold" color="primary.main" fontFamily="monospace">
                  {dashboard.partnership.referralCode}
                </Typography>
                <IconButton onClick={handleCopyReferralCode} color="primary">
                  <ContentCopy />
                </IconButton>
              </Box>
            </Paper>

            {/* Tabs */}
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Overview" />
              <Tab label="Performance" />
              <Tab label="Tier Info" />
            </Tabs>

            {/* Overview Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                      Contact Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{dashboard.partnership.email}</Typography>
                    </Box>
                    {dashboard.partnership.phone && (
                      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{dashboard.partnership.phone}</Typography>
                      </Box>
                    )}
                    {dashboard.partnership.contactPerson && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">{dashboard.partnership.contactPerson}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Partnership Details */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Partnership Details
                      </Typography>
                      <IconButton size="small" onClick={handleOpenCustomRateDialog} title="Set Custom Commission Rate">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box py={0.5}>
                      <Typography variant="caption" color="text.secondary">Type</Typography>
                      <Typography variant="body2" fontWeight="medium">{dashboard.partnership.partnershipTypeName}</Typography>
                    </Box>
                    <Box py={0.5}>
                      <Typography variant="caption" color="text.secondary">Activated</Typography>
                      <Typography variant="body2" fontWeight="medium">{formatDate(dashboard.partnership.activatedAt)}</Typography>
                    </Box>
                    <Box py={0.5}>
                      <Typography variant="caption" color="text.secondary">Commission Rate</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color={dashboard.partnership.customCommissionRate ? 'warning.main' : 'text.primary'}
                        >
                          {dashboard.partnership.customCommissionRate || dashboard.currentTier.currentCommissionRate}%
                        </Typography>
                        {dashboard.partnership.customCommissionRate && (
                          <Chip label="Custom" color="warning" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Performance Tab */}
            <TabPanel value={activeTab} index={1}>
              <Typography variant="h6" gutterBottom>
                Year-to-Date Performance
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <TrendingUp color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {dashboard.ytdStats.totalBookings.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Bookings
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <AttachMoney color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(dashboard.ytdStats.totalRevenue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Revenue
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <AttachMoney color="warning" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(dashboard.ytdStats.totalCommissions)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Commission
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <TrendingUp color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {dashboard.ytdStats.conversionRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Conversion Rate
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Top Referrals
              </Typography>
              {dashboard.topReferrals.map((referral, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body1" fontWeight="medium">{referral.customerName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {referral.totalBookings} bookings • First referred {formatDate(referral.firstReferredAt)}
                      </Typography>
                    </Box>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {formatCurrency(referral.totalRevenue)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </TabPanel>

            {/* Tier Info Tab */}
            <TabPanel value={activeTab} index={2}>
              <Typography variant="h6" gutterBottom>
                Current Tier: {dashboard.currentTier.displayName}
              </Typography>

              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                  {dashboard.currentTier.currentCommissionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Commission Rate
                </Typography>

                {dashboard.currentTier.progressToNextTier !== null && (
                  <>
                    <Box mt={3} mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Progress to Next Tier
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={dashboard.currentTier.progressToNextTier}
                        sx={{ height: 8, borderRadius: 4, mt: 1 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(dashboard.currentTier.volumeToNextTier || 0)} more to reach next tier
                    </Typography>
                  </>
                )}
              </Paper>

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Tier Benefits
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(dashboard.currentTier.benefits).map(([key, value]) => (
                  <Grid item key={key} xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {value ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Block color="disabled" fontSize="small" />
                      )}
                      <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex" gap={1}>
            {dashboard?.partnership?.isActive && (
              <>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<RestartAlt />}
                  onClick={(e) => setResetMenuAnchor(e.currentTarget)}
                >
                  Reset Volumes
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<Block />}
                  onClick={() => setSuspendDialogOpen(true)}
                >
                  Suspend
                </Button>
                {onDeactivate && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={onDeactivate}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
            {dashboard?.partnership && !dashboard.partnership.isActive && onReactivate && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={onReactivate}
              >
                Reactivate
              </Button>
            )}
          </Box>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </Box>
      </DialogActions>

      {/* Reset Volumes Menu */}
      <Menu
        anchorEl={resetMenuAnchor}
        open={Boolean(resetMenuAnchor)}
        onClose={() => setResetMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleResetVolumes('yearly')}>
          <Box display="flex" flexDirection="column">
            <Typography variant="body2" fontWeight="bold">Reset Yearly (YTD)</Typography>
            <Typography variant="caption" color="text.secondary">
              Reset year-to-date volumes to zero
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => handleResetVolumes('monthly')}>
          <Box display="flex" flexDirection="column">
            <Typography variant="body2" fontWeight="bold">Reset Monthly (MTD)</Typography>
            <Typography variant="caption" color="text.secondary">
              Reset month-to-date volumes to zero
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Custom Commission Rate Dialog */}
      <Dialog
        open={customRateDialogOpen}
        onClose={() => setCustomRateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Custom Commission Rate</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Custom rates override the default type/tier-based commission rate for all future bookings.
            </Alert>
            <TextField
              autoFocus
              fullWidth
              label="Commission Rate (%)"
              type="number"
              value={customRateValue}
              onChange={(e) => setCustomRateValue(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Enter a value between 0 and 100"
            />
            {dashboard?.partnership?.customCommissionRate && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Current custom rate: <strong>{dashboard.partnership.customCommissionRate}%</strong>
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {dashboard?.partnership?.customCommissionRate && (
            <Button
              onClick={handleRemoveCustomRate}
              color="error"
              disabled={customRateLoading}
            >
              Remove Custom Rate
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setCustomRateDialogOpen(false)} disabled={customRateLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSetCustomRate}
            variant="contained"
            disabled={customRateLoading}
          >
            {customRateLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Partnership Dialog */}
      <Dialog
        open={suspendDialogOpen}
        onClose={() => setSuspendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Suspend Partnership</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              Suspending this partnership will prevent new referrals. Existing bookings will not be affected.
            </Alert>
            <TextField
              autoFocus
              fullWidth
              label="Reason for Suspension (Optional)"
              multiline
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="e.g., Pending compliance review, payment dispute, etc."
              helperText="This reason will be logged and visible to admins"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)} disabled={suspendLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSuspendPartnership}
            variant="contained"
            color="warning"
            disabled={suspendLoading}
          >
            {suspendLoading ? <CircularProgress size={24} /> : 'Suspend Partnership'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Partnership Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Partnership Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={editData.companyName}
              onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={editData.email}
              onChange={(e) => setEditData({ ...editData, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone"
              value={editData.phone}
              onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
            />
            <TextField
              fullWidth
              label="Contact Person"
              value={editData.contactPerson}
              onChange={(e) => setEditData({ ...editData, contactPerson: e.target.value })}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              placeholder="Admin notes, special instructions, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePartnership}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        confirmText="Reset"
        cancelText="Cancel"
      />
    </Dialog>
  );
};

export default PartnershipDetailModal;
